import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SignInPage from '../pages/SignInPage';

interface AdminRouteProps {
  children: React.ReactNode;
}

/** Who may open the admin pages. The /admin APIs behind them check too. */
function isAdminUser(user: { role?: string; username?: string; email?: string; plan_organization_id?: string } | null) {
  if (!user) return false;
  return (
    user.role === 'admin' ||
    user.username === 'chughspacerobot' ||
    user.username === 'chughrobot' ||
    user.email === 'aarushchugh1@gmail.com' ||
    user.email === 'aarush@paircraft.tech' ||
    // Dedicated org admin accounts (created via the org settings "Dedicated Organization Admin" feature)
    Boolean(user.plan_organization_id)
  );
}

/**
 * The admin pages' door.
 *
 * A signed-out visitor gets the platform's own sign-in page — the same one
 * /sign-in renders, tenant header and all — rather than being bounced to the
 * home page: on a club's own site there is otherwise no way in with a
 * ChessDirector account, since the club login is for members. It lands back
 * here, so signing in shows the page that was asked for.
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <SignInPage staffSignIn redirectTo={`${location.pathname}${location.search}`} />;
  }

  if (!isAdminUser(user)) {
    // Say which account is signed in, and offer the way out: the usual reason
    // for landing here is being signed in as the wrong one.
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 px-4">
        <div className="text-center max-w-sm">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access denied</h1>
          <p className="text-gray-600">
            You must be an administrator to access this page.
          </p>
          <p className="text-sm text-gray-500 mt-3">
            Signed in as {user?.email || user?.username}.
          </p>
          <button
            type="button"
            onClick={logout}
            className="mt-6 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-100"
          >
            Sign in with a different account
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminRoute;
